import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { BookOpen, Loader2, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  PermissionsMultiSelectDialog,
  type PermissionOption,
} from "@/components/ui/permissions-multi-select-dialog";
import { PermissionsReferenceDialog, type GrantingRole } from "./PermissionsReferenceDialog";
import { CreateRoleDialog } from "./CreateRoleDialog";
import { EditRoleDialog } from "./EditRoleDialog";
import type { RoleInstance } from "@/stores/admin/models";

const SCOPE_VARIANT: Record<string, "default" | "secondary" | "outline"> = {
  org: "default",
  building: "secondary",
  project: "outline",
};

const RolesTable = observer(() => {
  const { rolesStore } = useStores();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleInstance | null>(null);
  const [permissionsDialogRole, setPermissionsDialogRole] = useState<RoleInstance | null>(null);
  const [referenceOpen, setReferenceOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ code: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    rolesStore.loadAll().catch((e) => {
      toast.error(e instanceof Error ? e.message : "Failed to load roles");
    });
  }, [rolesStore]);

  const permissionOptions = useMemo<PermissionOption[]>(
    () =>
      rolesStore.permissionsList.map((p) => ({
        code: p.code,
        name: p.name,
        description: p.description,
        category: p.category,
      })),
    [rolesStore.permissionsList]
  );

  // Invert role.permissionCodes into permission-code → granting-roles, so the
  // reference modal can show which roles include each permission. Active roles
  // are listed before deprecated ones, alphabetically within each group.
  const rolesByPermission = useMemo(() => {
    const map = new Map<string, GrantingRole[]>();
    for (const role of rolesStore.rolesList) {
      for (const code of role.permissionCodes) {
        const list = map.get(code) ?? [];
        list.push({ code: role.code, name: role.name, deprecated: role.deprecated });
        map.set(code, list);
      }
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        if (a.deprecated !== b.deprecated) return a.deprecated ? 1 : -1;
        return a.name.localeCompare(b.name);
      });
    }
    return map;
  }, [rolesStore.rolesList]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rolesStore.rolesList;
    return rolesStore.rolesList.filter((r) => {
      return (
        r.code.toLowerCase().includes(query) ||
        r.name.toLowerCase().includes(query) ||
        (r.description ?? "").toLowerCase().includes(query) ||
        r.scope.toLowerCase().includes(query)
      );
    });
  }, [rolesStore.rolesList, search]);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await rolesStore.deleteRole(deleteTarget.code);
      toast.success(`Role "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete role");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSavePermissions = async (codes: string[]) => {
    const role = permissionsDialogRole;
    if (!role) return;
    try {
      await rolesStore.setPermissions(role.code, codes);
      toast.success(`Updated permissions for "${role.name}"`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update permissions");
      throw e;
    }
  };

  if (rolesStore.isLoading && rolesStore.rolesList.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (rolesStore.error && rolesStore.rolesList.length === 0) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-destructive">
        Error loading roles: {rolesStore.error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          placeholder="Search by code, name, scope, or description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1"
        />
        <Button variant="outline" onClick={() => setReferenceOpen(true)}>
          <BookOpen className="mr-2 h-4 w-4" />
          Permissions
        </Button>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New role
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Code</TableHead>
              <TableHead className="w-[200px]">Name</TableHead>
              <TableHead className="w-[120px]">Scope</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead className="w-[200px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                  {search.trim()
                    ? "No roles match your search."
                    : "No roles defined yet. Create the first one above."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((role) => (
                <TableRow key={role.code}>
                  <TableCell className="font-mono text-xs">{role.code}</TableCell>
                  <TableCell>
                    <div className="font-medium">{role.name}</div>
                    {role.description ? (
                      <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                        {role.description}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Badge variant={SCOPE_VARIANT[role.scope] ?? "outline"}>{role.scope}</Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-mx-2 h-8 px-2 font-normal"
                      onClick={() => setPermissionsDialogRole(role)}
                    >
                      <ShieldCheck className="mr-2 h-4 w-4 text-muted-foreground" />
                      {role.permissionCodes.length} permission
                      {role.permissionCodes.length === 1 ? "" : "s"}
                    </Button>
                  </TableCell>
                  <TableCell>
                    {role.deprecated ? (
                      <Badge variant="outline" className="text-muted-foreground">
                        Deprecated
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditingRole(role)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDeleteTarget({ code: role.code, name: role.name })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <CreateRoleDialog open={createOpen} onOpenChange={setCreateOpen} />

      <EditRoleDialog
        role={editingRole}
        open={editingRole !== null}
        onOpenChange={(open) => {
          if (!open) setEditingRole(null);
        }}
      />

      <PermissionsMultiSelectDialog
        open={permissionsDialogRole !== null}
        onOpenChange={(open) => {
          if (!open) setPermissionsDialogRole(null);
        }}
        permissions={permissionOptions}
        initialSelected={permissionsDialogRole?.permissionCodes.slice() ?? []}
        title={
          permissionsDialogRole
            ? `Edit permissions for ${permissionsDialogRole.name}`
            : "Edit permissions"
        }
        description={
          permissionsDialogRole
            ? `Choose which permissions are granted to anyone with the ${permissionsDialogRole.code} role.`
            : undefined
        }
        onSave={handleSavePermissions}
      />

      <PermissionsReferenceDialog
        open={referenceOpen}
        onOpenChange={setReferenceOpen}
        permissions={rolesStore.permissionsList}
        rolesByPermission={rolesByPermission}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete role"
        description={
          deleteTarget
            ? `Delete role "${deleteTarget.name}" (${deleteTarget.code})? Users currently assigned this role in org_user_roles will keep the row, but the role will no longer grant any permissions. This cannot be undone.`
            : ""
        }
        confirmText={isDeleting ? "Deleting…" : "Delete"}
        confirmVariant="destructive"
      />
    </div>
  );
});

export default RolesTable;
